/**
 * @file PreferencesPanel.jsx
 * @author Rishikesh
 * @date 2024-09-25
 * @description Component of lead to show preference details
 */
import {
  Box,
  Divider,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import React from "react";
import PushPinIcon from "@mui/icons-material/PushPin";
import { Edit } from "@mui/icons-material";

export const PreferencesPanel = ({
  isLoading,
  courseCountryData,
  countryList,
  courseList,
  handleEditPreferences,
}) => {
  const getCourseNameById = (courseId) => {
    const course = courseList.find((course) => course.id === courseId);
    return course ? course.coursename : "Unknown Course";
  };

  const getCountryNameById = (countryId) => {
    const country = countryList.find((country) => country.id === countryId);
    return country ? country.country_name : "Unknown Country";
  };

  return (
    <Box
      width="100%"
      height={200}
      borderRadius="12px"
      px="12px"
      py={1}
      bgcolor="#F7F7F7"
      display="flex"
      flexDirection="column"
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {isLoading ? (
          <Skeleton variant="rounded" sx={{ mb: 1 }} width={140} height={24} />
        ) : (
          <Typography fontWeight={600} fontSize={18} color="#222222">
            Preferences
          </Typography>
        )}
        {!isLoading && (
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={handleEditPreferences}
          >
            Edit
          </Button>
        )}
      </Stack>
      <Divider />
      <List dense sx={{ width: "100%", mt: 0.5, flex: 1, overflowY: "auto" }}>
        {isLoading
          ? Array.from({ length: 7 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                sx={{ mt: 1 }}
                width={"100%"}
                height={32}
              />
            ))
          : courseCountryData.map((item, idx) => (
              <React.Fragment key={item.countryid}>
                <Stack direction="row" alignItems="center">
                  <ListItem
                    sx={{
                      paddingLeft: 0,
                      display: "flex",
                      alignItems: "center",
                      minWidth: 180,
                      width: "auto",
                    }}
                  >
                    <PushPinIcon
                      sx={{
                        color: "#f57c00",
                        marginRight: "8px",
                        fontSize: "18px",
                      }}
                    />
                    <Typography>
                      {getCountryNameById(item.countryid)}
                    </Typography>
                  </ListItem>
                  <Typography>-</Typography>
                  <Stack
                    direction="row"
                    paddingLeft="40px"
                    gap={0.8}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    {item.courses.map((course, index) => {
                      const courseName = getCourseNameById(course);
                      return (
                        <Chip
                          key={index}
                          label={courseName}
                          variant="outlined"
                          sx={{
                            borderRadius: "20px",
                            padding: "18px 12px",
                            fontWeight: "500",
                            bgcolor: "#EAEAEA",
                            border: "none",
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Stack>
                {idx < courseCountryData.length - 1 && (
                  <Divider sx={{ my: 1 }} />
                )}
              </React.Fragment>
            ))}
      </List>
    </Box>
  );
};
